import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, ArrowLeft, CheckCircle, CreditCard } from 'lucide-react';
import Navigation from '../components/Navigation';
import { useTranslation } from 'react-i18next';

const GroupDetail = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [group, setGroup] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const [referrerEmail, setReferrerEmail] = useState('');
  const [requestNote, setRequestNote] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [groupRes, roleRes] = await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/my-role`),
        ]);
        setGroup(groupRes.data);
        setMyRole(roleRes.data);
      } catch {
        toast.error(t('groupDetail.errors.load'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [groupId, t]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      await api.post(`/groups/${groupId}/join`);
      toast.success(t('groupDetail.toasts.joinSuccess'));
      const roleRes = await api.get(`/groups/${groupId}/my-role`);
      setMyRole(roleRes.data);
      navigate(`/groups/${groupId}/space`);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          t('groupDetail.errors.join')
      );
    } finally {
      setJoining(false);
    }
  };

  const handleSubmitJoinRequest = async (e) => {
    e.preventDefault();
    if (!group) return;

    setRequestSubmitting(true);
    try {
      await api.post(`/groups/${groupId}/join-requests`, {
        referrer_name: referrerName,
        referrer_email: referrerEmail,
        note: requestNote,
      });

      toast.success(t('groupDetail.toasts.requestSent'));
      setRequestDialogOpen(false);
      setReferrerName('');
      setReferrerEmail('');
      setRequestNote('');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupDetail.errors.request')
      );
    } finally {
      setRequestSubmitting(false);
    }
  };

  const goToGroupSpace = () => {
    navigate(`/groups/${groupId}/space`);
  };

  const handleStartSubscription = async () => {
    try {
      setSubscribing(true);
      const { data } = await api.post('/checkout/membership', { groupId });

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      toast.success(t('groupDetail.toasts.subscriptionActivated'));
      const [roleRes, groupRes] = await Promise.allSettled([
        api.get(`/groups/${groupId}/my-role`),
        api.get(`/groups/${groupId}`),
      ]);
      if (roleRes.status === 'fulfilled') setMyRole(roleRes.value.data);
      if (groupRes.status === 'fulfilled') setGroup(groupRes.value.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('groupDetail.errors.subscription')
      );
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <p className="text-slate-600">{t('groupDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <p className="text-slate-600">{t('groupDetail.notFound')}</p>
        </div>
      </div>
    );
  }

  const isMember = !!myRole?.role;
  const requiresSubscription = group.require_subscription;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/groups')}
          className="mb-4"
        >
          <ArrowLeft size={16} className="mr-2" />
          {t('groupDetail.back')}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <span>{group.name}</span>
              {isMember && (
                <Badge variant="default" className="flex items-center gap-1">
                  <CheckCircle size={14} />
                  {t('groupDetail.badges.member')}
                </Badge>
              )}
              {requiresSubscription && (
                <Badge variant="secondary">
                  {t('groupDetail.badges.subscriptionRequired')}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="flex items-center gap-2 text-sm">
              <Users size={16} />
              {group.member_count || 0}{' '}
              {t('groupDetail.members')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <h3 className="font-semibold mb-1">
                {t('groupDetail.aboutTitle')}
              </h3>
              <p className="text-slate-700">{group.description}</p>
            </section>

            {group.membership_price > 0 && (
              <section>
                <h3 className="font-semibold mb-1">
                  {t('groupDetail.membershipTitle')}
                </h3>
                <p className="text-slate-700">
                  {t('groupDetail.membershipPrice', {
                    currency: group.currency,
                    price: group.membership_price,
                  })}
                </p>
              </section>
            )}

            <div className="flex flex-wrap gap-3">
              {isMember ? (
                <Button onClick={goToGroupSpace}>
                  {t('groupDetail.actions.goToSpace')}
                </Button>
              ) : requiresSubscription ? (
                <Button
                  onClick={handleStartSubscription}
                  disabled={subscribing}
                >
                  <CreditCard size={16} className="mr-2" />
                  {subscribing
                    ? t('groupDetail.actions.subscribing')
                    : t('groupDetail.actions.subscribe')}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleJoin}
                    disabled={joining}
                  >
                    {joining
                      ? t('groupDetail.actions.joining')
                      : t('groupDetail.actions.join')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRequestDialogOpen(true)}
                  >
                    {t('groupDetail.actions.requestJoin')}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Join Request Dialog */}
      <Dialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('groupDetail.requestDialog.title')}
            </DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4 mt-2"
            onSubmit={handleSubmitJoinRequest}
          >
            <div>
              <Label htmlFor="referrerName">
                {t('groupDetail.requestDialog.referrerName')}
              </Label>
              <Input
                id="referrerName"
                value={referrerName}
                onChange={(e) => setReferrerName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="referrerEmail">
                {t('groupDetail.requestDialog.referrerEmail')}
              </Label>
              <Input
                id="referrerEmail"
                type="email"
                value={referrerEmail}
                onChange={(e) => setReferrerEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="requestNote">
                {t('groupDetail.requestDialog.note')}
              </Label>
              <Textarea
                id="requestNote"
                rows={4}
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRequestDialogOpen(false)}
              >
                {t('groupDetail.requestDialog.cancel')}
              </Button>
              <Button type="submit" disabled={requestSubmitting}>
                {requestSubmitting
                  ? t('groupDetail.requestDialog.submitting')
                  : t('groupDetail.requestDialog.submit')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupDetail;
